import { DiscountCodeReference } from '@commercetools/platform-sdk';
import { Price, PriceWithDiscount } from '@services/product';

export interface DiscountCode {
  discountCodeRef: DiscountCodeReference,
  discountCode: {
    name: string,
    code: string,
  }
}

export interface LineItemAttribute {
  name: string,
  value: string,
  label: string
}

export interface LineItemProductVariant {
  id: number,
  imageUrl?: string,
  attributes?: LineItemAttribute[],
  sku?: string
}

export interface LineItem {
  id: string,
  name: string,
  quantity: number,
  price: PriceWithDiscount,
  totalPrice: Price,
  variant: LineItemProductVariant,
  productId: string,
  productType?: { name: string } | string
}

export interface CartOutOfStockItems {
  id: string,
  availableQuantity: number,
}

export interface Cart {
  id: string,
  anonymousId?: string,
  version: number,
  cartState?: string,
  totalLineItemQuantity: number,
  totalPrice: Price,
  shippingPrice?: PriceWithDiscount,
  lineItems: LineItem[],
  discountCodes?: DiscountCode[],
}

export interface FluentStoreAvailabilityResponse {
  availability: Record<string, FluentRefObject[]>;
  locations: FluentStore[];
}

export interface FluentStore {
  name: string;
  openingSchedule: FluentOpeningSchedule;
  primaryAddress: FluentAddress;
  ref: string;
}

export interface FluentOpeningSchedule {
  allHours: boolean;
  monStart: number;
  monEnd: number;
  tueStart: number;
  tueEnd: number;
  wedStart: number;
  wedEnd: number;
  thuStart: number;
  thuEnd: number;
  friStart: number;
  friEnd: number;
  satStart: number;
  satEnd: number;
  sunStart: number;
  sunEnd: number;
}

export interface FluentAddress {
  street: string;
  city: string;
  country: string;
  postcode: string;
  state: string;
  latitude: number;
  longitude: number;
}

export interface FluentRefObject {
  ref: string;
}

export interface InStoreAvailabilityItem {
  name: string;
  ref: string;
  address: FluentAddress;
  openHours: string[];
  isAvailable: boolean;
}

export const CartAction = {
  ADD_LINE_ITEM: 'ADD_LINE_ITEM',
  CHANGE_LINE_ITEM_QUANTITY: 'CHANGE_LINE_ITEM_QUANTITY',
  REMOVE_LINE_ITEM: 'REMOVE_LINE_ITEM',
  ADD_DISCOUNT_CODE: 'ADD_DISCOUNT_CODE',
  REMOVE_DISCOUNT_CODE: 'REMOVE_DISCOUNT_CODE',
  SET_SHIPPING_ADDRESS: 'SET_SHIPPING_ADDRESS',
  SET_SHIPPING_METHOD: 'SET_SHIPPING_METHOD'
} as const;

export interface CartAddressAction {
  firstName?: string;
  lastName?: string;
  streetName?: string;
  streetNumber?: string;
  additionalStreetInfo?: string;
  postalCode?: string;
  city?: string;
  region?: string;
  state?: string;
  country?: string;
  company?: string;
  department?: string;
  building?: string;
  apartment?: string;
  pOBox?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  fax?: string;
  additionalAddressInfo?: string;
  externalId?: string;
  key?: string;
}

export interface CartAddLineItemAction {
  action: typeof CartAction.ADD_LINE_ITEM;
  productId: string;
  variantId?: number;
  sku?: string;
  quantity: number;
}

export interface CartChangeLineItemQuantityAction {
  action: typeof CartAction.CHANGE_LINE_ITEM_QUANTITY;
  lineItemId: string;
  quantity: number;
}

export interface CartRemoveLineItemAction {
  action: typeof CartAction.REMOVE_LINE_ITEM;
  lineItemId: string;
}

export interface CartAddDiscountCode {
  action: typeof CartAction.ADD_DISCOUNT_CODE;
  code: string;
}

export interface CartRemoveDiscountCode {
  action: typeof CartAction.REMOVE_DISCOUNT_CODE;
  discountCode: { id: string };
}

export interface CartSetShippingAddressAction {
  action: typeof CartAction.SET_SHIPPING_ADDRESS;
  address: CartAddressAction;
}

export interface CartSetShippingMethodAction {
  action: typeof CartAction.SET_SHIPPING_METHOD;
  shippingMethodId: string;
}

export type CartUpdateAction =
  | CartAddLineItemAction
  | CartChangeLineItemQuantityAction
  | CartRemoveLineItemAction
  | CartAddDiscountCode
  | CartRemoveDiscountCode
  | CartSetShippingAddressAction
  | CartSetShippingMethodAction;
